import { RuntimeConfig } from '@/types/configuration.type';
import LogoPNG from '@/images/logo_nk.png';

/**
 * Local dev mock for `GET /api/v1/configuration` (NK tenant), used until the
 * BE endpoint exists — see docs/adr/07. Real response shape (style/feature/
 * exterior) confirmed at 联调.
 */
export const mockConfiguration: RuntimeConfig = {
  style: {
    logo: LogoPNG,
    favicon: '/favicon_nk.ico',
    theme: {
      primary: '#34709b',
      secondary: '#EDF5FB',
      bg: '#1a5077',
    },
    wording: {
      patientSingular: { en: 'citizen', da: 'borger' },
      patientPlural: { en: 'citizens', da: 'borgere' },
    },
    defaultLanguage: 'da',
    regionCode: ['+45', '+86'],
  },
  feature: {
    faelles: { a: { enabled: true }, o: { enabled: false } },
    ufsEndpoint: 'https://develop.ufs.levvel.cloud',
    transfer: {
      enabled: true,
      targets: {
        // Batch 011 naming unification (裁决 2): `key` is an IDENTIFIER and is
        // now `mvh` everywhere; `display` stays `MVH-BA` because that is a
        // presentation value, as is the tenant's domain segment.
        peers: [
          {
            key: 'mvh',
            enabled: true,
            display: 'MVH-BA',
            // Non-empty on purpose: the transfer-log modal resolves an event's
            // institution mark by looking the actor's tenant key up in this
            // list (ADR-9 ⑧ — name snapshotted, logo looked up).
            logo: '/favicon_nk.ico',
          },
        ],
      },
    },
    sms: { optionOnlyAdminRole: true },
  },
  exterior: { firebase: null },
};

/**
 * Second-tenant mock (MVH-BA). Used in dev to demo "different tenant → different
 * render" via ?tenant=mvh. MVH-BA has no SSO, so its login page drops the SSO
 * button (vs NK). It is the receiving end of transfers, so it carries
 * `transfer.sources` instead of `targets`. Real tenants resolve by host at
 * runtime — this switch is dev-only.
 */
export const mockConfigurationMvh: RuntimeConfig = {
  style: {
    logo: LogoPNG,
    favicon: '/favicon_nk.ico',
    theme: {
      primary: '#34709b',
      secondary: '#EDF5FB',
      bg: '#1a5077',
    },
    wording: {
      patientSingular: { en: 'citizen', da: 'borger' },
      patientPlural: { en: 'citizens', da: 'borgere' },
    },
    defaultLanguage: 'da',
    regionCode: ['+45'],
  },
  feature: {
    faelles: { a: { enabled: false }, o: { enabled: false } },
    transfer: {
      enabled: true,
      sources: {
        peers: [
          {
            key: 'naestved',
            enabled: true,
            display: 'Naestved Kommune',
            logo: '',
          },
        ],
      },
    },
    sms: { optionOnlyAdminRole: true },
  },
  exterior: { firebase: null },
};

/** Dev-only: pick a tenant mock by ?tenant= query (default NK). */
export const mockConfigByTenant = (tenant: string | null): RuntimeConfig => {
  switch (tenant) {
    case 'mvh':
      return mockConfigurationMvh;
    default:
      return mockConfiguration;
  }
};
